import { createRouter, createWebHistory } from 'vue-router'
import { getUser } from '@/services/db'
import { useImpersonation } from '@/composables/useImpersonation'

async function isAuthenticated(): Promise<boolean> {
  const userId = localStorage.getItem('currentUserId')
  if (!userId) return false
  const user = await getUser(userId)
  return !!user
}


const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    { path: '/', name: 'landing', component: () => import('@/views/LandingView.vue'), meta: { public: true } },
    { path: '/e/:slug', name: 'event-public', component: () => import('@/views/events/PublicEventView.vue'), meta: { public: true } },
    { path: '/e/:slug/team/:code', name: 'event-team', component: () => import('@/views/events/TeamPublicView.vue'), meta: { public: true } },
    { path: '/dossier/:token', name: 'dossier-public', component: () => import('@/views/sponsors/DossierPublicView.vue'), meta: { public: true } },
    { path: '/sponsor-portal/:token', name: 'sponsor-portal', component: () => import('@/views/sponsors/SponsorPortalView.vue'), meta: { public: true } },
    { path: '/enroll/:slugOrToken', name: 'enrollment-form', component: () => import('@/views/enrollment/EnrollmentFormView.vue'), meta: { public: true } },
    { path: '/participant-link', name: 'participant-link', component: () => import('@/views/auth/ParticipantLinkView.vue'), meta: { public: true } },
    { path: '/e/:slug/my', name: 'participant-dashboard', component: () => import('@/views/events/ParticipantEventView.vue'), meta: { requiresAuth: true } },
    { path: '/e/:slug/enrollment/:enrollmentId', name: 'enrollment-status', component: () => import('@/views/enrollment/EnrollmentStatusView.vue'), meta: { requiresAuth: true } },
    { path: '/login', name: 'login', component: () => import('@/views/auth/LoginView.vue'), meta: { public: true } },
    { path: '/register', name: 'register', component: () => import('@/views/auth/RegisterView.vue'), meta: { public: true } },
    { path: '/onboarding', name: 'onboarding', component: () => import('@/views/onboarding/OnboardingView.vue'), meta: { requiresAuth: true, skipOrgCheck: true } },
    { path: '/dashboard', name: 'dashboard', component: () => import('@/views/DashboardView.vue'), meta: { requiresAuth: true } },
    { path: '/join/:token', name: 'join', component: () => import('@/views/JoinView.vue'), meta: { requiresAuth: false } },
    { path: '/events', name: 'events', component: () => import('@/views/events/EventsListView.vue'), meta: { requiresAuth: true } },
    { path: '/events/new', name: 'event-new', component: () => import('@/views/events/EventFormView.vue'), meta: { requiresAuth: true } },
    { path: '/events/:id', name: 'event-detail', component: () => import('@/views/events/EventDetailView.vue'), meta: { requiresAuth: true } },
    { path: '/events/:id/day', name: 'event-day', component: () => import('@/views/events/EventDayView.vue'), meta: { requiresAuth: true, fullScreen: true } },
    { path: '/events/:id/tv', name: 'event-tv', component: () => import('@/views/events/EventTVView.vue'), meta: { requiresAuth: true, fullScreen: true } },
    { path: '/events/:id/edit', name: 'event-edit', component: () => import('@/views/events/EventFormView.vue'), meta: { requiresAuth: true } },
    { path: '/organizations', name: 'organizations', component: () => import('@/views/organizations/OrganizationsView.vue'), meta: { requiresAuth: true } },
    { path: '/organizations/:id/settings', name: 'org-settings', component: () => import('@/views/organizations/OrgSettingsView.vue'), meta: { requiresAuth: true } },
    { path: '/calendar', name: 'season-calendar', component: () => import('@/views/SeasonCalendarView.vue'), meta: { requiresAuth: true } },
    { path: '/pricing', name: 'pricing', component: () => import('@/views/PricingView.vue'), meta: { requiresAuth: true } },

    { path: '/admin', name: 'admin', component: () => import('@/views/admin/SuperAdminView.vue'), meta: { requiresAuth: true, requiresSuperAdmin: true } },
    { path: '/:pathMatch(.*)*', redirect: '/' }
  ]
})

router.beforeEach(async (to, _from, next) => {
  const isPublic = to.meta.public === true
  const requiresAuth = to.meta.requiresAuth === true

  const authenticated = await isAuthenticated()

  if (isPublic) {
    if (authenticated && (to.name === 'login' || to.name === 'register' || to.name === 'landing')) {
      const redirect = to.query.redirect as string
      return next(redirect || { name: 'dashboard' })
    }
    return next()
  }

  if (requiresAuth && !authenticated) {
    return next({ name: 'login', query: { redirect: to.fullPath } })
  }

  if (to.meta.requiresSuperAdmin) {
    const { getRealUserId } = useImpersonation()
    const userId = getRealUserId()
    if (userId) {
      const user = await getUser(userId)
      if (!user?.isSuperAdmin) return next({ name: 'dashboard' })
    } else {
      return next({ name: 'dashboard' })
    }
  }

  next()
})

export default router
